import _find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import { fetcher } from '../saga/fetcher.saga';
import { compactForIpadView } from './ipadView';

/**
 * Grilles comparables : type draft_synchro de la modification et action de
 * la route pendingCompare (comparer ; supprimer = 'cancel' + Action).
 */
export const GRID_KINDS = {
	// Produits d'une catégorie.
	product: { type: 'category_product', action: 'CategoryProduct' },
	// Suppléments d'une catégorie de suppléments.
	supplement: { type: 'category_supplement_product', action: 'CategorySupplementProduct' },
	// Composition d'un menu : ses niveaux et ses produits (id_table = le menu).
	menu: { types: ['menu_level', 'product_product'], action: 'Menu' },
};

const kindOf = kind => GRID_KINDS[kind] || GRID_KINDS.product;

/**
 * Une modification en cours (non déployée) touche-t-elle la grille de cette
 * catégorie ? Même filtre que « Modifications en cours » : journée affichée
 * et zone de travail.
 * @param {Object} dayChangesList state.entity.dayChanges.list
 * @param {String} date YYYY-MM-DD
 * @param {String|null} idZone sessionStorage id_zone
 * @param {String} idCategory catégorie ou catégorie de suppléments
 * @param {'product'|'supplement'|'menu'} [kind]
 * @returns {Boolean}
 */
export function hasCategoryProductChange(dayChangesList, date, idZone, idCategory, kind) {
	const day = dayChangesList && dayChangesList[date];
	if (!day || !idCategory) return false;
	return (day.undeployedModifications || []).some(
		change =>
			(kindOf(kind).types || [kindOf(kind).type]).includes(change.type) &&
			change.id_table === idCategory &&
			change.id_zone == idZone
	);
}

/**
 * Recale old_value sur la caisse puis renvoie l'avant / après de la grille.
 * @returns {Promise<{found, before, after, productsBefore, productsAfter}>}
 */
export function fetchCategoryProductCompare({ idMenu, idCategory, date, idZone, kind }) {
	const action = kindOf(kind).action;
	return fetcher(
		'pendingCompare',
		'POST',
		true,
		{ id_menu: idMenu, id_category: idCategory, date, id_zone: idZone },
		'/' + action[0].toLowerCase() + action.slice(1)
	).then(json => {
		const result = json && json.result;
		if (!result || typeof result.found !== 'boolean') {
			throw new Error('pendingCompare: réponse inattendue');
		}
		return result;
	});
}

/**
 * Supprime la modification en cours de la catégorie : le brouillon revient à
 * la caisse (journée et zone de travail lues par le serveur).
 * @returns {Promise<Boolean>} faux s'il n'y avait plus rien en cours
 */
export function cancelCategoryProductChanges({ idMenu, idCategory, kind }) {
	return fetcher(
		'pendingCompare',
		'POST',
		true,
		{ id_menu: idMenu, id_category: idCategory },
		'/cancel' + kindOf(kind).action
	).then(json => {
		const result = json && json.result;
		if (!result || typeof result.cancelled !== 'boolean') {
			throw new Error('pendingCompare: réponse inattendue');
		}
		return result.cancelled;
	});
}

/**
 * Cases d'une grille dans l'ordre des poids, une case vide par poids manquant
 * (pas de compaction : on compare les positions réelles).
 */
function toCells(links, products) {
	const cells = [];
	sortBy(links, link => Number(link.weight)).forEach(link => {
		while (cells.length < Number(link.weight)) cells.push(null);
		const product = _find(products, p => p.id === link.id_product) || {};
		cells.push({
			id: link.id_product,
			name: product.name || '',
			id_color: product.id_color,
			active: product.active,
		});
	});
	return cells;
}

/**
 * Liaisons telles que l'iPad les affiche : la règle de la Vue caisse
 * (compactForIpadView) appliquée aux positions, puis chaque produit reçoit
 * le poids de sa case. Les indisponibles cachés disparaissent de la liste.
 * @param {Object[]} links liaisons category_product
 * @param {Object[]} products infos produit (active)
 * @param {Boolean} hideDisabled la caisse cache les indisponibles
 * @returns {Object[]}
 */
export function toIpadLinks(links, products, hideDisabled) {
	const items = [];
	sortBy(links || [], link => Number(link.weight)).forEach(link => {
		while (items.length < Number(link.weight)) items.push({ isSpace: true });
		const product = _find(products || [], p => p.id === link.id_product) || {};
		items.push({ link, active: product.active });
	});
	return compactForIpadView(items, hideDisabled).reduce((acc, item, index) => {
		if (!item.isSpace) acc.push({ ...item.link, weight: index });
		return acc;
	}, []);
}

/**
 * Suppléments tels que la caisse les affiche : enchaînés par poids, sans case
 * vide ; les indisponibles restent (grisés). Chaque supplément reçoit le
 * poids de sa place.
 * @param {Object[]} links liaisons category_supplement_product
 * @returns {Object[]}
 */
export function toIpadSupplementLinks(links) {
	return sortBy(links || [], link => Number(link.weight)).map((link, index) => ({
		...link,
		weight: index,
	}));
}

/**
 * Compare la grille de la caisse (avant) et celle du brouillon (après).
 * Les cases sont numérotées à partir de 1 dans l'ordre de lecture.
 * @returns {{
 *   before: Object[], after: Object[],
 *   middle: Object[], removedBelow: Object[],
 *   moves: {id, name, id_color, from: Number|null, to: Number|null}[],
 *   unchanged: Boolean
 * }} cases null = case vide ; middle : cases de l'après annotées
 *   (status added | moved | same | removed, from / to = numéros de case)
 */
export function buildCategoryDiff({ before, after, productsBefore, productsAfter }) {
	const beforeCells = toCells(before || [], productsBefore || []);
	const afterCells = toCells(after || [], productsAfter || []);
	const caseOf = (cells, id) => {
		const index = cells.findIndex(cell => cell && cell.id === id);
		return index < 0 ? null : index + 1;
	};

	const middle = afterCells.map((cell, index) => {
		if (!cell) return null;
		const from = caseOf(beforeCells, cell.id);
		const to = index + 1;
		let status = 'same';
		if (from === null) status = 'added';
		else if (from !== to) status = 'moved';
		return { ...cell, status, from, to };
	});

	// Retiré : carré rouge à la case qu'il occupait, si elle est libre dans
	// l'après ; sinon rangé sous la grille.
	const removedBelow = [];
	beforeCells.forEach((cell, index) => {
		if (!cell || caseOf(afterCells, cell.id) !== null) return;
		const removed = { ...cell, status: 'removed', from: index + 1, to: null };
		if (index >= middle.length) {
			while (middle.length < index) middle.push(null);
			middle.push(removed);
		} else if (middle[index] === null) {
			middle[index] = removed;
		} else {
			removedBelow.push(removed);
		}
	});

	const moves = [];
	const seen = {};
	beforeCells.concat(afterCells).forEach(cell => {
		if (!cell || seen[cell.id]) return;
		seen[cell.id] = true;
		const inAfter = _find(afterCells, c => c && c.id === cell.id);
		moves.push({
			id: cell.id,
			name: (inAfter || cell).name,
			id_color: (inAfter || cell).id_color,
			from: caseOf(beforeCells, cell.id),
			to: caseOf(afterCells, cell.id),
		});
	});

	return {
		before: beforeCells,
		after: afterCells,
		middle,
		removedBelow,
		moves,
		unchanged: moves.every(m => m.from === m.to),
	};
}

/**
 * Liaisons product_product d'un niveau, au format d'une grille (id_product =
 * le produit du menu), et infos produit avec la couleur propre au menu.
 */
function levelGrid(side, idLevel, products) {
	const links = idLevel ? (side.links || []).filter(link => link.id_menu_level === idLevel) : [];
	return {
		links: links.map(link => ({ ...link, id_product: link.id_product_child })),
		products: links.map(link => {
			const product = _find(products || [], p => p.id === link.id_product_child) || {};
			return { ...product, id: link.id_product_child, id_color: link.id_color || product.id_color };
		}),
	};
}

const formatPrice = price => (parseFloat(price) || 0).toFixed(2).replace('.', ',');

/**
 * Ce qui change sur un produit resté dans son niveau : prix dans le menu,
 * obligatoire ou non.
 * @returns {String[]} libellés (clés de traduction et valeurs) à afficher
 */
function linkChanges(before, after) {
	const notes = [];
	if (parseFloat(before.price || 0) !== parseFloat(after.price || 0)) {
		notes.push(`${formatPrice(before.price)} → ${formatPrice(after.price)}`);
	}
	if (Number(before.required || 0) !== Number(after.required || 0)) {
		notes.push(Number(after.required) ? 'required' : 'optional');
	}
	return notes;
}

/**
 * Compare la composition d'un menu, niveau par niveau.
 * @param {Object} result réponse de la comparaison (before / after : levels, links)
 * @param {Function} [toLinks] (links, products) => liaisons affichées
 *   (rendu iPad) ; par défaut, positions du back-office
 * @returns {{sections: Object[], unchanged: Boolean}} une section par
 *   niveau : id, title, beforeTitle, status (added | removed | same), renamed,
 *   from / to (rang du niveau, à partir de 1), diff (buildCategoryDiff, cases
 *   annotées de notes : prix, obligatoire)
 */
export function buildMenuDiff(result, toLinks = links => links) {
	const sorted = levels => sortBy(levels || [], level => Number(level.weight));
	const beforeLevels = sorted(result.before && result.before.levels);
	const afterLevels = sorted(result.after && result.after.levels);
	const rank = (levels, id) => {
		const index = levels.findIndex(level => level.id === id);
		return index < 0 ? null : index + 1;
	};

	const levelIds = afterLevels.map(level => level.id).concat(
		beforeLevels.filter(level => rank(afterLevels, level.id) === null).map(level => level.id)
	);

	const sections = levelIds.map(id => {
		const beforeLevel = _find(beforeLevels, level => level.id === id);
		const afterLevel = _find(afterLevels, level => level.id === id);
		const before = levelGrid(result.before || {}, beforeLevel ? id : null, result.productsBefore);
		const after = levelGrid(result.after || {}, afterLevel ? id : null, result.productsAfter);
		const diff = buildCategoryDiff({
			before: toLinks(before.links, before.products),
			after: toLinks(after.links, after.products),
			productsBefore: before.products,
			productsAfter: after.products,
		});

		// Prix / obligatoire changés sur un produit resté dans le niveau.
		let notes = false;
		diff.middle = diff.middle.map(cell => {
			if (!cell || cell.status === 'added' || cell.status === 'removed') return cell;
			const b = _find(before.links, link => link.id_product === cell.id);
			const a = _find(after.links, link => link.id_product === cell.id);
			const changes = b && a ? linkChanges(b, a) : [];
			if (!changes.length) return cell;
			notes = true;
			return { ...cell, notes: changes };
		});

		const from = beforeLevel ? rank(beforeLevels, id) : null;
		const to = afterLevel ? rank(afterLevels, id) : null;
		let status = 'same';
		if (!beforeLevel) status = 'added';
		else if (!afterLevel) status = 'removed';
		const renamed = beforeLevel && afterLevel && beforeLevel.name !== afterLevel.name;
		return {
			id,
			title: (afterLevel || beforeLevel).name || '',
			beforeTitle: beforeLevel ? beforeLevel.name || '' : null,
			status,
			renamed: !!renamed,
			from,
			to,
			diff,
			unchanged: status === 'same' && !renamed && from === to && diff.unchanged && !notes,
		};
	});

	return { sections, unchanged: sections.every(section => section.unchanged) };
}
